import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { User } from '../../models/users/user';
import { Merchant } from '../../models/merchant/merchant';
import { NavMenuItem } from '../../models/ui/navMenuItem';
import { Account } from '../../models/account/account';
import { TransactionFacade } from '../../models/transactions/transactionFacade';
import { Customer } from '../../models/customer/customer';
import { ChargebackFacade } from '../../models/chargebacks/chargebackFacade';
import { ListItem } from '../../../../../../libs/Shared/components/model/listItem';
import { Logging } from '../logging';
import { Observable, Subject } from 'rxjs';

export enum RoutingDataItem {
  CustomerId = 'customerId',
  TransactionId = 'transactionId',
}

@Injectable({
  providedIn: 'root',
})
export class StorageService implements OnInit, OnDestroy {
  private AUTH_HEADER: string = 'AUTH_HEADER';
  private ROUTING_DATA_KEY: string = 'ROUTING_DATA';
  private static ALL_ACCESS_NAMESPACES: string = 'ALL_ACCESS_NAMESPACES';

  private IMPERSONATE_ACCESS_MAP_KEY: string = 'IMPERSONATE_ACCESS_MAP';
  private IMPERSONATING_USER_KEY: string = 'IMPERSONATE_USER_KEY';
  private IMPERSONATING_SESSION_KEY: string = 'IMPERSONATING_SESSION';
  private IMPERSONATE_END_REDIRECT_KEY: string = 'END_IMPERSONATION_ROUTE';

  // User session info
  private SESSION_KEY: string = 'USER_SESSION_KEY';
  //private SESSION_ACTIVE_KEY:string = "SESSION_ACTIVE_KEY";
  private USER_KEY: string = 'USER_KEY';
  private ACCESS_MAP_KEY: string = 'ACCESS_MAP';
  private NAV_KEY: string = 'NAV_KEY';
  private NUMBER_OF_MY_BUSINESSES_KEY = 'NUMBER_OF_MY_BUSINESSES_KEY';
  private MY_MERCHANTS_KEY = 'MY_MERCHANTS_KEY';
  private MY_ACCOUNT_KEY: string = 'MY_ACCOUNT';
  private SYNCING_STATEMENTS_KEY: string = 'SYNCING_STATEMENTS_KEY';
  private SHOW_SUPPORT_INFO_KEY: string = 'SHOW_SUPPORT_INFO_KEY';
  private SELECTED_ACCOUNT_KEY: string = 'SELECTED_ACCOUNT';
  private SELECTED_LOCATION_KEY: string = 'SELECTED_LOCATION';
  private SELECTED_USER_KEY: string = 'SELECTED_USER';
  private SELECTED_TRANSACTION_KEY: string = 'SELECTED_TRANSACTION';
  //private SELECTED_PAYMENT_METHOD_KEY:string = "SELECTED_PAYMENT_METHOD";
  private SELECTED_CUSTOMER_KEY: string = 'SELECTED_CUSTOMER';
  private SELECTED_CHARGEBACK_KEY: string = 'SELECTED_CHARGEBACK';
  private CONVERSATION_ID: string = 'CONVERSATION_ID';
  private BRAND_LIST_KEY: string = 'BRAND_LIST_KEY';
  private SELECTED_INVOICE_ID_KEY: string = 'SELECTED_INVOICE_ID';

  public currentUserObservable = new Subject<User | null>();
  public currentNavObservable = new Subject<NavMenuItem[]>();

  public selectedLocationObservable = new Subject<Merchant | null>();
  public brandListObservable = new Subject<ListItem[]>();

  // public sessionIsActive: boolean = false;

  // nav-related
  // note: we want to serialize this information when impersonation
  // starts so that we can revert to it when impersonation ends.
  // public selectedAccountId: string | undefined;
  // public selectedLocationId: string | undefined;
  // public selectedProcessingId: string | undefined;
  // public selectedPaymentMethodId: string | undefined;
  // public selectedUserId: string | undefined;
  // public selectedTransactionId: string | undefined;
  // public selectedCustomerId: string | undefined;

  // Chat
  private conversationId: string | undefined;
  private openSupportChatConversations: string[] = [];

  static sessionState = new Map();

  constructor() {}

  ngOnInit(): void {
    this.conversationId =
      localStorage.getItem(this.CONVERSATION_ID) ?? undefined;
  }

  public clearSession() {
    this.removeCurrentUser();
    this._removeRoleAccessMap();
    localStorage.removeItem(this.IMPERSONATING_USER_KEY);
    localStorage.removeItem(this.IMPERSONATING_SESSION_KEY);
    this.removeImpersonateRoleAccessMap();
    this.removeRoutingData();
    this.removeCurrentNav();
    this.removeNumberOfMyBusinesses();
    this.removeMyMerchants();
    this.removeSelectedAccount();
    this.removeSelectedLocation();
    this.removeSelectedUser();
    this.removeSelectedTransaction();
    this.removeSelectedChargeBack();
    this.removeSelectedCustomer();
    this.removeMyAccount();
    this.removeSyncingStatements();
    this.removeShowSupportInfo();
    // temp
    //localStorage.removeItem("NUMBER_OF_MY_MERCHANTS_KEY");
    //localStorage.removeItem("ORGANIZATION_KEY");
    this._removeAllAccessNamespaces();
  }

  ngOnDestroy() {
    this.currentUserObservable.unsubscribe();
    this.selectedLocationObservable.unsubscribe();
    this.currentNavObservable.unsubscribe();
  }

  // TOOD: should this be in userService?
  // serializes the user's session and stores it away for later
  public setImpersonatingSession(endImpersonationRedirectUrl: string | null) {
    let sessionData: any = {};

    if (endImpersonationRedirectUrl) {
      localStorage.setItem(
        this.IMPERSONATE_END_REDIRECT_KEY,
        endImpersonationRedirectUrl,
      );
    }

    //sessionData.user = localStorage.getItem(this.USER_KEY);
    sessionData.accessMap = localStorage.getItem(this.ACCESS_MAP_KEY);
    sessionData.nav = localStorage.getItem(this.NAV_KEY);
    // number of my businesses...
    // my merchants...
    sessionData.myAccount = this.getMyAccount();
    sessionData.myMerchants = this.getMyMerchants();
    sessionData.selectedAccount = this.getSelectedAccount();

    sessionData.selectedLocation = this.getSelectedLocation();
    sessionData.selectedUser = this.getSelectedUser();
    sessionData.selectedTransaction = this.getSelectedTransaction();
    sessionData.selectedCustomer = this.getSelectedCustomer();
    sessionData.selectedChargeback = this.getSelectedChargeback();
    //sessionData.selectedProcessingId = localStorage.getItem(this.BUSINESS_KEY);
    //sessionData.selectedPaymentMethodId = localStorage.getItem(this.BUSINESS_KEY);

    let user: string | null = localStorage.getItem(this.USER_KEY);

    if (!user) {
      throw 'no user found in session';
    }

    localStorage.setItem(this.IMPERSONATING_USER_KEY, user);
    localStorage.setItem(
      this.IMPERSONATING_SESSION_KEY,
      JSON.stringify(sessionData),
    );

    this.removeMyAccount();
    this.removeSelectedAccount();
    this.removeSelectedCustomer();
    this.removeSelectedLocation();
    this.removeSelectedTransaction();
    this.removeSelectedChargeBack();
    this.removeSelectedUser();
    this.removeSyncingStatements();
  }

  public getNextRedirectUrlAndClear(): string | null {
    let route = localStorage.getItem(this.IMPERSONATE_END_REDIRECT_KEY);
    localStorage.removeItem(this.IMPERSONATE_END_REDIRECT_KEY);
    return route;
  }

  // TOOD: Should this be in userService?
  public restoreUserSession() {
    let sessionData: any = localStorage.getItem(this.IMPERSONATING_SESSION_KEY);
    let sUser: string | null = localStorage.getItem(
      this.IMPERSONATING_USER_KEY,
    );

    if (!sessionData) {
      throw 'no session found to restore';
    }
    if (!sUser) {
      throw 'no impersonating user found in session';
    }

    let user = new User(JSON.parse(sUser as string));
    let session = JSON.parse(sessionData);

    this.setCurrentUser(user);
    localStorage.setItem(this.ACCESS_MAP_KEY, session.accessMap);
    localStorage.setItem(this.NAV_KEY, session.nav);
    // number of my businesses...
    // my merchants...

    if (session.myAccount) {
      this.setMyAccount(session.myAccount);
    } else {
      this.removeMyAccount();
    }
    if (session.myMerchants) {
      this.setMyMerchants(session.myMerchants);
    } else {
      this.removeMyMerchants();
    }
    if (session.selectedAccount) {
      this.setSelectedAccount(session.selectedAccount);
    } else {
      this.removeSelectedAccount();
    }
    if (session.selectedLocation) {
      this.setSelectedLocation(session.selectedLocation);
    } else {
      this.removeSelectedLocation();
    }
    if (session.selectedUser) {
      this.setSelectedUser(session.selectedUser);
    } else {
      this.removeSelectedUser();
    }

    if (session.selectedTransaction) {
      this.setSelectedTransaction(session.selectedTransaction);
    } else {
      this.removeSelectedTransaction();
    }
    if (session.selectedCustomer) {
      this.setSelectedCustomer(session.selectedCustomer);
    } else {
      this.removeSelectedCustomer();
    }
    if (session.selectedChargeback) {
      this.setSelectedChargeback(session.selectedChargeback);
    } else {
      this.removeSelectedChargeBack();
    }
    if (session.syncingStatements) {
      this.setSyncingStatements(session.syncingStatements);
    } else {
      this.removeSyncingStatements();
    }
    //sessionData.selectedProcessingId = localStorage.getItem(this.BUSINESS_KEY);
    //sessionData.selectedPaymentMethodId = localStorage.getItem(this.BUSINESS_KEY);

    localStorage.removeItem(this.IMPERSONATING_SESSION_KEY);
    localStorage.removeItem(this.IMPERSONATING_USER_KEY);
    localStorage.removeItem(this.IMPERSONATE_ACCESS_MAP_KEY);

    this.currentNavObservable.next(sessionData.nav);
  }

  public clearUserSession() {
    localStorage.removeItem(this.SESSION_KEY);
  }

  public setBrandList(brandList: ListItem[]): void {
    localStorage.setItem(this.BRAND_LIST_KEY, JSON.stringify(brandList));
    this.brandListObservable.next(brandList);
  }

  public getBrandList(): ListItem[] {
    let brandList = localStorage.getItem(this.BRAND_LIST_KEY);

    if (brandList != undefined) {
      return JSON.parse(brandList);
    } else {
      return [];
    }
  }

  public getNumberOfMyBusinesses(): number {
    let data = localStorage.getItem(this.NUMBER_OF_MY_BUSINESSES_KEY);

    if (data) {
      return parseInt(data);
    } else {
      return 0;
    }
  }

  public removeNumberOfMyBusinesses() {
    localStorage.removeItem(this.NUMBER_OF_MY_BUSINESSES_KEY);
  }

  public setNumberOfMyBusinesses(numberOfBusinesses: number) {
    return localStorage.setItem(
      this.NUMBER_OF_MY_BUSINESSES_KEY,
      numberOfBusinesses.toString(),
    );
  }

  public getMyMerchants(): Partial<Merchant>[] {
    let data = localStorage.getItem(this.MY_MERCHANTS_KEY);

    if (data) {
      return JSON.parse(data);
    } else {
      return [];
    }
  }

  public removeMyMerchants() {
    localStorage.removeItem(this.MY_MERCHANTS_KEY);
  }

  public setMyMerchants(merchants: Partial<Merchant>[]) {
    return localStorage.setItem(
      this.MY_MERCHANTS_KEY,
      JSON.stringify(merchants),
    );
  }

  public getRoutingData(): any {
    let data = localStorage.getItem(this.ROUTING_DATA_KEY);

    if (data) {
      return JSON.parse(data);
    }
    return null;
  }

  public addRoutingData(key: RoutingDataItem, value: any): void {
    let data = this.getRoutingData();
    if (data == null) {
      data = {};
    }
    data[key] = value;
    localStorage.setItem(this.ROUTING_DATA_KEY, JSON.stringify(data));
  }

  public removeRoutingData() {
    localStorage.removeItem(this.ROUTING_DATA_KEY);
  }

  get sessionIsActive() {
    return localStorage.getItem(this.USER_KEY) != null;
  }

  public isSessionActive() {
    return localStorage.getItem(this.USER_KEY) != null;
  }

  public getAuthHeader(): string | null {
    return localStorage.getItem(this.AUTH_HEADER);
  }

  public setAuthHeader(auth: string) {
    localStorage.setItem(this.AUTH_HEADER, auth);
  }

  public removeAuthHeader(): void {
    localStorage.removeItem(this.AUTH_HEADER);
  }

  public setAllAccessNamespaces(items: any[]): void {
    localStorage.setItem(
      StorageService.ALL_ACCESS_NAMESPACES,
      JSON.stringify(items),
    );
  }

  public static getAllAccessNamespaces(): any[] {
    let allBits = localStorage.getItem(StorageService.ALL_ACCESS_NAMESPACES);
    if (allBits != null) {
      return JSON.parse(allBits);
    } else {
      return [];
    }
  }

  private _removeAllAccessNamespaces(): void {
    localStorage.removeItem(StorageService.ALL_ACCESS_NAMESPACES);
  }

  public setCurrentNav(items: NavMenuItem[]): void {
    localStorage.setItem(this.NAV_KEY, JSON.stringify(items));
    this.currentNavObservable.next(items);
  }

  public getCurrentNav(menu: string = ''): NavMenuItem[] {
    let nav = localStorage.getItem(this.NAV_KEY);

    if (nav != undefined) {
      return JSON.parse(nav).filter((item: NavMenuItem) => item.menu === menu);
    } else {
      return JSON.parse('[]');
    }
  }

  public watchCurrentNavChange(): Observable<any> {
    return this.currentNavObservable.asObservable();
  }

  public removeCurrentNav(): void {
    localStorage.removeItem(this.NAV_KEY);
    this.currentNavObservable.next([]);
  }

  // Get the user who is impersonating another user
  public getImpersonatingUser(): User | undefined {
    let sUser = localStorage.getItem(this.IMPERSONATING_USER_KEY);

    if (sUser != null) {
      try {
        return new User(JSON.parse(sUser as string));
      } catch (e) {
        Logging.verbose(
          'storage-service',
          'getImpersonatingUser',
          'Error parsing user',
          e,
        );
      }
    }
    return undefined;
  }

  public setCurrentUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.currentUserObservable.next(user);
  }

  public getCurrentUser(): User | undefined {
    let sUser = localStorage.getItem(this.USER_KEY);
    if (sUser) {
      return new User(JSON.parse(sUser as string));
    }

    //throw 'no current user';
    return undefined;
  }

  public removeCurrentUser(): void {
    localStorage.removeItem(this.USER_KEY);
    this.currentUserObservable.next(null);
  }

  public watchCurrentUserChange(): Observable<any> {
    return this.currentUserObservable.asObservable();
  }

  public setRoleAccessMap(map: Map<string, boolean>): void {
    localStorage.setItem(
      this.ACCESS_MAP_KEY,
      JSON.stringify(Object.fromEntries(map)),
    );
  }

  public getRoleAccessMap(): Map<string, boolean> {
    let sMap = localStorage.getItem(this.ACCESS_MAP_KEY);
    if (sMap != null) {
      try {
        return new Map(Object.entries(JSON.parse(sMap)));
      } catch (e) {
        throw e;
      }
    } else {
      return new Map<string, boolean>();
    }
  }

  private _removeRoleAccessMap(): void {
    localStorage.removeItem(this.ACCESS_MAP_KEY);
  }

  // Get the access map of the person who is impersonating another user
  public getImpersonateRoleAccessMap(): Map<string, boolean> {
    let sMap = localStorage.getItem(this.IMPERSONATE_ACCESS_MAP_KEY);
    if (sMap != null) {
      try {
        return new Map(Object.entries(JSON.parse(sMap)));
      } catch (e) {
        throw e;
      }
    } else {
      return new Map<string, boolean>();
    }
  }

  public removeImpersonateRoleAccessMap(): void {
    localStorage.removeItem(this.IMPERSONATE_ACCESS_MAP_KEY);
  }

  public setMyAccount(account: Account) {
    localStorage.setItem(this.MY_ACCOUNT_KEY, JSON.stringify(account));
  }

  public getMyAccount(): Account | undefined {
    let sAccount = localStorage.getItem(this.MY_ACCOUNT_KEY);

    if (sAccount != null && sAccount != '') {
      return JSON.parse(sAccount) as Account;
    }
    return undefined;
  }

  public removeMyAccount(): void {
    localStorage.removeItem(this.MY_ACCOUNT_KEY);
  }

  public setSelectedAccount(account: Account) {
    StorageService.sessionState.set(this.SELECTED_ACCOUNT_KEY, account);
    localStorage.setItem(this.SELECTED_ACCOUNT_KEY, JSON.stringify(account));
  }

  public getSelectedAccount(): Account | undefined {
    if (StorageService.sessionState.get(this.SELECTED_ACCOUNT_KEY)) {
      return StorageService.sessionState.get(this.SELECTED_ACCOUNT_KEY);
    }

    let thing = localStorage.getItem(this.SELECTED_ACCOUNT_KEY);
    if (thing) {
      try {
        let b = new Account(JSON.parse(thing));
        StorageService.sessionState.set(this.SELECTED_ACCOUNT_KEY, b);
        return b;
      } catch {
        localStorage.removeItem(this.SELECTED_ACCOUNT_KEY);
      }
    }

    return undefined;
  }

  public removeSelectedAccount(): void {
    StorageService.sessionState.delete(this.SELECTED_ACCOUNT_KEY);
    localStorage.removeItem(this.SELECTED_ACCOUNT_KEY);
  }

  public setSelectedLocation(organization: Merchant) {
    StorageService.sessionState.set(this.SELECTED_LOCATION_KEY, organization);
    localStorage.setItem(
      this.SELECTED_LOCATION_KEY,
      JSON.stringify(organization),
    );
    this.selectedLocationObservable.next(organization);
  }

  public getSelectedLocation(): Merchant | undefined {
    if (StorageService.sessionState.get(this.SELECTED_LOCATION_KEY)) {
      return StorageService.sessionState.get(this.SELECTED_LOCATION_KEY);
    }

    let obj = localStorage.getItem(this.SELECTED_LOCATION_KEY);
    if (obj) {
      let m = new Merchant(JSON.parse(obj));
      StorageService.sessionState.set(this.SELECTED_LOCATION_KEY, m);
      return m;
    } else {
      return undefined;
    }
  }

  public removeSelectedLocation() {
    StorageService.sessionState.delete(this.SELECTED_LOCATION_KEY);
    localStorage.removeItem(this.SELECTED_LOCATION_KEY);
    this.selectedLocationObservable.next(null);
  }

  public watchCurrentLocationChange(): Observable<any> {
    return this.selectedLocationObservable.asObservable();
  }

  public setSelectedInvoiceId(invoiceId: string) {
    localStorage.setItem(this.SELECTED_INVOICE_ID_KEY, invoiceId);
  }

  public getSelectedInvoiceId(): string | undefined {
    let invoiceId = localStorage.getItem(this.SELECTED_INVOICE_ID_KEY);
    if (invoiceId) {
      return invoiceId;
    }
    return undefined;
  }

  public removeSelectedInvoiceId() {
    localStorage.removeItem(this.SELECTED_INVOICE_ID_KEY);
  }

  public setSelectedUser(user: User) {
    StorageService.sessionState.set(this.SELECTED_USER_KEY, user);
    localStorage.setItem(this.SELECTED_USER_KEY, JSON.stringify(user));
  }

  public getSelectedUser(): User | undefined {
    if (StorageService.sessionState.get(this.SELECTED_USER_KEY)) {
      return StorageService.sessionState.get(this.SELECTED_USER_KEY);
    }

    let obj = localStorage.getItem(this.SELECTED_USER_KEY);
    if (obj) {
      let u = new User(JSON.parse(obj));
      StorageService.sessionState.set(this.SELECTED_USER_KEY, u);
      return u;
    } else {
      return undefined;
    }
  }

  public removeSelectedUser(): void {
    StorageService.sessionState.delete(this.SELECTED_USER_KEY);
    localStorage.removeItem(this.SELECTED_USER_KEY);
  }

  public setSelectedTransaction(trans: TransactionFacade) {
    StorageService.sessionState.set(this.SELECTED_TRANSACTION_KEY, trans);
    localStorage.setItem(this.SELECTED_TRANSACTION_KEY, JSON.stringify(trans));
  }

  public getSelectedTransaction(): TransactionFacade | undefined {
    if (StorageService.sessionState.get(this.SELECTED_TRANSACTION_KEY)) {
      return StorageService.sessionState.get(this.SELECTED_TRANSACTION_KEY);
    }
    let obj = localStorage.getItem(this.SELECTED_TRANSACTION_KEY);
    if (obj) {
      let t = new TransactionFacade(JSON.parse(obj));
      StorageService.sessionState.set(this.SELECTED_TRANSACTION_KEY, t);
      return t;
    }
    return undefined;
  }

  public removeSelectedTransaction(): void {
    StorageService.sessionState.delete(this.SELECTED_TRANSACTION_KEY);
    localStorage.removeItem(this.SELECTED_TRANSACTION_KEY);
  }

  public setSelectedCustomer(customer: Customer) {
    StorageService.sessionState.set(this.SELECTED_CUSTOMER_KEY, customer);
    localStorage.setItem(this.SELECTED_CUSTOMER_KEY, JSON.stringify(customer));
  }

  public getSelectedCustomer(): Customer | undefined {
    if (StorageService.sessionState.get(this.SELECTED_CUSTOMER_KEY)) {
      return StorageService.sessionState.get(this.SELECTED_CUSTOMER_KEY);
    }
    let obj = localStorage.getItem(this.SELECTED_CUSTOMER_KEY);
    if (obj) {
      let c = new Customer(JSON.parse(obj));
      StorageService.sessionState.set(this.SELECTED_CUSTOMER_KEY, c);
      return c;
    } else {
      return undefined;
    }
  }

  public removeSelectedCustomer(): void {
    StorageService.sessionState.delete(this.SELECTED_CUSTOMER_KEY);
    localStorage.removeItem(this.SELECTED_CUSTOMER_KEY);
  }

  public setSelectedChargeback(chargeback: ChargebackFacade) {
    if (chargeback) {
      StorageService.sessionState.set(this.SELECTED_CHARGEBACK_KEY, chargeback);
      localStorage.setItem(
        this.SELECTED_CHARGEBACK_KEY,
        JSON.stringify(chargeback),
      );
    } else {
      throw 'chargeback is null. If you want to remove it, call removeSelectedChargeback() instead.';
    }
  }

  public getSelectedChargeback(): ChargebackFacade | undefined {
    let cachedChargeback = StorageService.sessionState.get(
      this.SELECTED_CHARGEBACK_KEY,
    );

    if (cachedChargeback) {
      return StorageService.sessionState.get(this.SELECTED_CHARGEBACK_KEY);
    }
    let obj = localStorage.getItem(this.SELECTED_CHARGEBACK_KEY);

    if (obj) {
      let c = new ChargebackFacade(JSON.parse(obj));
      StorageService.sessionState.set(this.SELECTED_CHARGEBACK_KEY, c);
      return c;
    } else {
      return undefined;
    }
  }

  public removeSelectedChargeBack() {
    StorageService.sessionState.delete(this.SELECTED_CHARGEBACK_KEY);
    localStorage.removeItem(this.SELECTED_CHARGEBACK_KEY);
  }

  public getConversationId(): string | undefined {
    return this.conversationId;
  }

  public setConversationId(conversationId: string) {
    this.conversationId = conversationId;
    localStorage.setItem(this.CONVERSATION_ID, this.conversationId);
  }

  public unsetConversationId() {
    this.conversationId = undefined;
    localStorage.removeItem(this.CONVERSATION_ID);
  }

  public setSyncingStatements(syncing: boolean) {
    localStorage.setItem(this.SYNCING_STATEMENTS_KEY, syncing.toString());
  }

  public getSyncingStatements(): boolean {
    return localStorage.getItem(this.SYNCING_STATEMENTS_KEY) === 'true';
  }

  public removeSyncingStatements() {
    localStorage.removeItem(this.SYNCING_STATEMENTS_KEY);
  }

  public setShowSupportInfo(show: boolean) {
    localStorage.setItem(this.SHOW_SUPPORT_INFO_KEY, show.toString());
  }

  public getShowSupportInfo(): boolean {
    return localStorage.getItem(this.SHOW_SUPPORT_INFO_KEY) === 'true';
  }

  public removeShowSupportInfo() {
    localStorage.removeItem(this.SHOW_SUPPORT_INFO_KEY);
  }

  // TODO (jacob): Currently unused
  /*private isSupportChatConversationOpen(conversationId: string): boolean {
        return this.openSupportChatConversations.indexOf(conversationId) > -1;
    }

    public addSupportChatConversation(conversationId: string) {
        if (!this.isSupportChatConversationOpen(conversationId)) {
            this.openSupportChatConversations.push(conversationId);
        } else {
            throw new Error(`Conversation '${conversationId}' is already open.`);
        }
    }

    public removeSupportChatConversation(conversationId: string) {
        this.openSupportChatConversations = this.openSupportChatConversations
            .filter(item => item !== conversationId);
    }*/
}
